import React, { useState } from "react";
import { Send, Loader2, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Lightweight modal feedback form. Posts to /api/feedback which stores the
 * message in Mongo and (if RESEND_API_KEY is configured) forwards it to
 * info@pawground.app. Renders nothing when `open` is false.
 */
const FeedbackDialog = ({ open, onOpenChange, defaultEmail = "" }) => {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (open) {
      setMessage("");
      setEmail(defaultEmail);
      setDone(false);
    }
  }, [open, defaultEmail]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Please write a longer message");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", { message: message.trim(), email: email.trim() || null });
      setDone(true);
      toast.success("Thanks for the feedback!");
      setTimeout(() => onOpenChange(false), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="feedback-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-ink/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-warm-lg border border-brand-border-warm p-6 sm:p-7 animate-fade-in-up">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest">Send feedback</div>
            <h2 className="font-heading text-2xl font-bold text-brand-ink mt-1">We'd love to hear from you</h2>
          </div>
          <button
            data-testid="feedback-dialog-close"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="rounded-full w-9 h-9 flex items-center justify-center text-brand-ink/60 hover:bg-brand-light-green hover:text-brand-forest transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-brand-forest text-white mx-auto flex items-center justify-center mb-3">
              <Send className="w-6 h-6" />
            </div>
            <p className="text-sm text-brand-ink/70">Your message is in — thank you for helping make Pawground better.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <textarea
              data-testid="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind? Bugs, feature ideas, missing parks…"
              rows={5}
              required
              className="w-full px-4 py-3 bg-brand-cream border border-brand-border-warm rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-brand-forest resize-none"
            />
            <input
              data-testid="feedback-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email (optional, in case we want to follow up)"
              className="w-full px-4 py-2.5 bg-brand-cream border border-brand-border-warm rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-brand-forest"
            />
            <button
              type="submit"
              data-testid="feedback-submit"
              disabled={submitting}
              className="w-full bg-brand-forest text-white font-semibold rounded-full py-3 hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send feedback
            </button>
            <p className="text-center text-[11px] text-brand-ink/55 pt-1 flex items-center justify-center gap-1">
              <Mail className="w-3 h-3" />
              Goes to info@pawground.app
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackDialog;
